import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { zql } from '../schema.js';
import { assertCan, EventLogs } from '../utils.js';
import { addEventLog, DeleteResourceSchema, EventLogResources, UpdateResourceStatusSchema } from './util.js';
import { AddProtocolSchema, deleteUnusedTherapySequences, getProtocolPrescribedNames, updateProtocol, UpdateProtocolSchema, updateProtocolTherapySequence, } from './utils/protocols.js';
async function findById(tx, id) {
    return await tx.run(zql.protocols.where('id', '=', id).one());
}
export const protocols = {
    add: defineMutator(AddProtocolSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'protocols.manage');
        const now = Date.now();
        const newProtocol = {
            id: uuidv7(),
            name: input.name,
            stage: input.stage[0] ?? null,
            diagnosisId: input.diagnosisId ?? null,
            cancerTypeId: input.cancerTypeId ?? null,
            isTrial: input.isTrial ?? false,
            totalCycles: input.totalCycles ?? 0,
            daysRepetition: input.daysRepetition ?? 0,
            notes: input.notes ?? null,
            createdAt: now,
            createdBy: ctx.user.id,
        };
        await tx.mutate.protocols.insert(newProtocol);
        const prescribedNames = await getProtocolPrescribedNames(tx, input.therapySequence ?? []);
        const therapySequences = [];
        const medicines = [];
        for (const ts of input.therapySequence ?? []) {
            const therapySequence = {
                id: uuidv7(),
                protocolId: newProtocol.id,
                day: ts.day,
                time: ts.time,
                isNote: ts.isNote ?? false,
                note: ts.note ?? null,
                carrierSolutionId: ts.carrierSolutionId[0],
                applicationFormId: ts.applicationFormId[0],
                duration: ts.duration,
                volume: ts.volume,
                volumeMode: ts.volumeMode[0],
                createdAt: now,
                createdBy: ctx.user.id,
                therapyNote: ts.therapyNote,
                pharmacyNote: ts.pharmacyNote,
            };
            therapySequences.push(therapySequence);
            for (const med of ts.medicine ?? []) {
                const prescribedName = prescribedNames.find(pn => pn.id === med.prescribedNameId[0]);
                if (med.prescribedNameId[0] && !prescribedName) {
                    throw new Error(`Prescribed name with id ${med.prescribedNameId[0]} not found`);
                }
                const medicine = {
                    id: uuidv7(),
                    therapySequenceId: therapySequence.id,
                    activeSubstanceId: prescribedName?.drugCatalogItemId ?? med.activeSubstanceId[0],
                    prescribedNameId: med.prescribedNameId[0],
                    autIdem: med.prescribedNameId[0] ? (med.autIdem ?? false) : false,
                    calculationMethod: med.calculationMethod[0],
                    regularDose: med.regularDose,
                    precisionDecimals: med.precisionDecimals,
                    notes: med.notes,
                    ageRanges: med.ageRanges,
                    createdAt: now,
                    createdBy: ctx.user.id,
                };
                medicines.push(medicine);
            }
        }
        await Promise.all(therapySequences.map(ts => tx.mutate.protocolTherapySequence.insert(ts)));
        await Promise.all(medicines.map(m => tx.mutate.protocolTherapySequenceMedicine.insert(m)));
        if (tx.location === 'server') {
            const patches = createPatch({}, newProtocol);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.protocol,
                resourceId: newProtocol.id,
                action: 'create',
                domain: 'configuration',
                event: EventLogs.PROTOCOL_CREATED,
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    protocolName: newProtocol.name,
                    protocolId: newProtocol.id,
                },
            }, ctx);
        }
    }),
    update: defineMutator(UpdateProtocolSchema, async ({ args: input, ctx, tx }) => {
        assertCan(ctx, 'protocols.manage');
        const { prev, updatedProtocol } = await updateProtocol(tx, ctx.user.id, input);
        const prescribedNames = await getProtocolPrescribedNames(tx, input.therapySequence ?? []);
        if (input.therapySequence) {
            await deleteUnusedTherapySequences(tx, ctx.user.id, prev.id, input.therapySequence);
            await Promise.all(input.therapySequence.map(ts => updateProtocolTherapySequence(tx, ctx.user.id, updatedProtocol.id, ts, prescribedNames, {
                protocolName: updatedProtocol.name ?? prev.name,
            })));
        }
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'protocols.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        await tx.mutate.protocols.update({
            id: args.id,
            status: args.status,
            updatedAt: now,
            updatedBy: ctx.user.id,
        });
        if (tx.location === 'server') {
            const patches = createPatch(prev, {
                ...prev,
                status: args.status,
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.protocol,
                resourceId: args.id,
                domain: 'configuration',
                event: args.status === 'inactive' ? EventLogs.PROTOCOL_DEACTIVATED : EventLogs.PROTOCOL_ACTIVATED,
                action: 'update',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: args.status === 'inactive' ? 'WARNING' : 'INFO',
                details: {
                    protocolName: prev.name,
                    protocolId: prev.id,
                },
            }, ctx);
        }
    }),
    delete: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'protocols.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedProtocol = {
            id: args.id,
            name: `${prev.name}_deleted_${now}`,
            deletedAt: now,
            deletedBy: ctx.user.id,
        };
        await tx.mutate.protocols.update(updatedProtocol);
        if (tx.location === 'server') {
            const patches = createPatch(prev, { ...prev, deletedAt: now, deletedBy: ctx.user.id });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.protocol,
                resourceId: prev.id,
                action: 'delete',
                domain: 'configuration',
                event: EventLogs.PROTOCOL_DELETED,
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'WARNING',
                details: {
                    protocolName: prev.name,
                    protocolId: prev.id,
                },
            }, ctx);
        }
    }),
};
//# sourceMappingURL=protocol.mutators.js.map